<script setup lang="ts">
import { useRoute } from 'vue-router'
import { ref } from 'vue'
import { getFloorMayLike, getHouseModelDetails, getSecretNumber } from '~/server/newhouse'
import type { FloorHouseModelDetailsVO } from '~/server/newhouse/interface'
import { computedStatusColor } from '~/utils/utils'
import { useCityInfoStore } from '~/stores/useCityInfo'
import {
  getAnliList,
  getInterestRate,
  getInterestRateNameList,
  getMaterialList,
  getSignUpConfig,
  secretNumberTrackingSave,
  simpleQueryCalculator,
} from '~/server/common'
import { userInfoAction } from '~/composables/userInfoAction'
import { useCommonAction } from '~/composables/useCommonAction'
import TopHeader from '~/components/TopHeader.vue'
import config from '~/common/config'
import { usePageTimeTracker } from '~/composables/usePageTimeTracker'

usePageTimeTracker()
const { city } = useCityInfoStore()

const iconPhoto = 'https://imgf1.fccs.com.cn/newhouse-mobile/house_type/icon-phone.png'
const iconDesc = 'https://imgf1.fccs.com.cn/newhouse-mobile/house_type/icon-desc.png'
const iconCall = 'https://imgf1.fccs.com.cn/newhouse-mobile/house_type/icon-call.png'

const route = useRoute()
const { data: detail } = await useAsyncData(() => getHouseModelDetails({
  cityId: city,
  houseModelId: route.params.houseModelId,
}), {
  transform: (res: any) => {
    return res.dataBody as FloorHouseModelDetailsVO
  },
})
// 猜你喜欢
const { data: MaybeLikeList } = await useAsyncData(() => getFloorMayLike({
  cityId: city,
  current: 1,
  pageSize: 5,
}), {
  transform: (res: any) => {
    return res.dataBody.list
  },
})

/**
 * 获取查询利率名称列表
 * 拿到利率ID后查询利率详情
 * 查询贷款计算器数据(简要)
 */
const { data: rateInfo } = await useAsyncData(() => getInterestRateNameList({ rateType: 3 }), {
  transform: (res: any) => {
    return res.dataBody[0]
  },
})

const { data: rateDetail } = await useAsyncData(() => getInterestRate({ loanTerm: 30, rateId: rateInfo.value.rateId }), {
  transform: (res: any) => {
    return res.dataBody
  },
})

const { data: calculator } = await useAsyncData(() => simpleQueryCalculator({
  businessInterestRate: rateDetail.value.rate, // 商业贷款利率，例如：0.049（公积金贷款不用填）
  businessPrice: (detail.value?.priceTotal ?? 0) * 0.7, // 商业贷款金额，单位：万元（商业贷款、组合贷款时填写）
  loanStyle: 2, // 贷款类型，1:公积金贷款，2:商业贷款，3:组合贷款
  loanTerm: 30, // 贷款年限，例如：30
  totalPrice: detail.value?.priceTotal, // 房价总额，单位：万元（组合贷款不用填）
}), {
  transform: (res: any) => {
    return res.dataBody as any
  },
})

/**
 * 获取报名配置
 */
const { data: configMap } = await useAsyncData(() => getSignUpConfig({
  configCodes: ['mHouseTypeGetRate', 'mHouseTypePercentage', 'mHouseTypeTaxation', 'mHouseTypeDiscount', 'mHouseTypeIntroduce', 'mFreeMeasuringRooms'].join(','),
}), {
  transform: (res: any) => {
    if (res.code === 0) {
      return res?.dataBody?.reduce((acc: any, item: any) => {
        acc[item.configCode] = item
        return acc
      }, {})
    }
    else {
      return {}
    }
  },
})
const userStore = useUserInfoStore()
const nuxtApp = useNuxtApp()
const uuid = ref('')
/**
 * 拨打电话-置业顾问
 */
const { handleGetAgentList } = useCommonAction()
const adviserId = handleGetAgentList(detail.value?.floorAdviserList || [])
const showPhoneModal = ref(false)
function callPhone() {
  getSecretNumber({
    cityId: city,
    floorId: detail.value?.floorId as number,
    type: 2, // 电话类型 1：楼盘电话；2：置业顾问
    numberId: adviserId, // 楼盘ID或置业顾问ID
  }).then((res) => {
    showPhoneModal.value = false
    if (res.code === 0 && res.dataBody) {
      const { phoneX, obtainResult } = res.dataBody
      const { userId } = userStore?.userInfo ?? {}
      uuid.value = nuxtApp.$getUuid()
      // 隐私号埋点统计
      secretNumberTrackingSave({
        businessDataId: 1,
        businessId: detail.value?.floorId as number,
        cityId: city,
        equipmentBrowseUuid: uuid.value,
        numberId: detail.value?.floorId as number,
        obtainResult,
        pageName: document.title,
        pageUrl: config.SSLWX + config.GLOBAL_HOST + route.path,
        portTypeId: 2,
        sectionId: 1,
        userId: userId || 0,
      })
      window.location.href = `tel:${phoneX}`
    }
    else {
      showToast(res?.message || '当前线路忙，请稍后再拨')
    }
  })
}

const { commonCollect } = userInfoAction()

/**
 * 咨询置业顾问
 * @param info
 */
async function handleCollect(info: any) {
  const config = configMap?.value[info?.configCode] || {}
  await commonCollect({
    floorId: detail.value?.floorId,
    configId: config?.configId,
    source: '新房/M版/户型详情',
    adviserList: detail.value?.floorAdviserList,
    ...info,
  })
}
/**
 * 获取装修案例列表
 */
const { data: caseList } = await useAsyncData(() => getAnliList({
  cityId: city,
  page: 1,
  pageSize: 10,
  room: detail.value?.room,
}), {
  transform: (res: any) => {
    return res.items
  },
})

/**
 * 获取家居建材列表
 */
const { data: materialList } = await useAsyncData(() => getMaterialList({
  cityId: city,
  page: 1,
  pageSize: 6,
}), {
  transform: (res: any) => {
    return res.items
  },
})

// VR
const panoramaPicList = computed(() => detail.value?.panoramaList ?? [])
// 户型图
const photoPicList = computed(() => {
  const { photoList, photo } = detail.value || {}
  return photoList ?? [photo]
})
// 样板间
const sampleRoomPicList = computed(() => detail.value?.sampleRoomPhotoList ?? [])

// VR列表长度
const panoramaListLen = computed(() => panoramaPicList.value.length)
// 户型图列表长度
const photoListLen = computed(() => photoPicList.value.length)
// 样板间列表长度
const sampleRoomListLen = computed(() => sampleRoomPicList.value.length)

// 户型类型
const houseTypeList = computed(() => {
  const typeList = []
  if (panoramaListLen.value > 0) {
    typeList.push({
      type: '户型VR',
      len: panoramaListLen.value,
      photoList: panoramaPicList.value,
    })
  }
  if (photoListLen.value > 0) {
    typeList.push({
      type: '户型图',
      len: photoListLen.value,
      photoList: photoPicList.value,
    })
  }
  if (sampleRoomListLen.value > 0) {
    typeList.push({
      type: '样板间',
      len: sampleRoomListLen.value,
      photoList: sampleRoomPicList.value,
    })
  }
  return typeList
})

const otherHouseTypeList = computed(() => detail.value?.otherModelList || [])
const similarHouseTypeList = computed(() => detail.value?.similarModelList || [])

const statusColor = computed(() => computedStatusColor(detail.value || {}))

const domain = ref(route.fullPath[route.fullPath.length - 1] === '/' ? `` : `${route.params.domain}/`)
/**
 *房贷计算器
 */
function toCalculator(price) {
  window.location.href = `//m.fccs.com/tools/loan.html?price=${price}`
}
//  前往楼栋详情
function handleToBuild() {
  window.location.href = `/${domain.value}newhouse/${detail.value?.floorId}/buildingView.html`
}
onMounted(() => {
  //  猜你喜欢是否已关注，客户端二次校验
  if (MaybeLikeList?.value?.length > 0) {
    userSaveCheck({
      numberId: MaybeLikeList.value.map(i => i.floorId),
      sortId: 5,
      cb: (res) => {
        const { code, dataBody } = res
        if (code === 0) {
          MaybeLikeList.value = MaybeLikeList.value.map((item) => {
            const obj = item
            if (dataBody[item.floorId] !== undefined) {
              obj.isFavorite = dataBody[item.floorId]
            }
            return obj
          })
        }
      },
    })
  }
})
</script>

<template>
  <div class="page-container">
    <!-- 顶部导航 -->
    <TopHeader title="户型详情" />
    <NewHouseSwiper :list="[...panoramaPicList, ...photoPicList, ...sampleRoomPicList]" :type-list="houseTypeList" />
    <!-- 基本信息 -->
    <div class="page-margin">
      <Card
        warp-class-name="info-padding"
        radius
      >
        <template #title>
          <div class="info">
            <div v-show="detail?.houseFrame" class="info-title">
              {{ detail?.title }}
            </div>
            <div class="info-tag">
              <Tag v-show="detail?.saleStatus && detail?.saleStatusId" :color="statusColor">
                {{ detail?.saleStatus }}
              </Tag>
              <Tag color="gray">
                {{ detail?.houseUse }}
              </Tag>
            </div>
          </div>
        </template>
        <template #extra>
          <div
            class="info-extra" @click="handleCollect({
              configCode: 'mHouseTypeIntroduce',
              message: '我刚浏览了这个楼盘的户型，请问可以介绍下吗？',
            })"
          >
            <div>咨询详情</div>
            <van-icon class="icon" name="arrow" />
          </div>
        </template>
        <div class="center-info-box">
          <!--          价格 -->
          <div v-show="detail?.priceTotal" class="li price">
            <div class="top">
              <span class="num">{{ detail?.priceTotal }}</span>万起
            </div>
            <div class="bottom">
              参考价格
            </div>
          </div>
          <div v-show="detail?.houseFrame" class="li house-model">
            {{ detail?.houseFrame ? detail?.houseFrame : '' }}
            <div class="bottom">
              户型
            </div>
          </div>
          <!--          建筑面积 -->
          <div v-show="detail?.area" class="li area">
            {{ detail?.area ? `${detail?.area}` : '' }}<span style="font-weight: unset;font-size: 11px;">㎡</span>
            <div class="bottom">
              建筑面积
            </div>
          </div>
        </div>
        <Description>
          <DescriptionItem v-if="detail?.direction" label="户型朝向">
            {{ detail?.direction }}
          </DescriptionItem>
          <DescriptionItem label="所属楼盘">
            <a class="green-txt" :href="goToFloorDetail(detail?.floorId)">{{ detail?.floor }}</a>
          </DescriptionItem>
          <DescriptionItem v-if="!!detail?.bindBuildingList?.length" label="所在楼栋">
            <div class="bind-building">
              <div @click="handleToBuild">
                {{
                  detail?.bindBuildingList?.map(item => `${item.buildingNum}#`).join('、')
                }}
              </div>
            </div>
          </DescriptionItem>
          <DescriptionItem v-if="detail?.layerLow && detail?.layerHigh && detail?.layerSum" label="楼层">
            第{{ detail?.layerLow || '-' }}~{{ detail?.layerHigh || '-' }}层（共{{ detail?.layerSum || '-' }}层）
          </DescriptionItem>
          <DescriptionItem label="得房率">
            <span
              @click="handleCollect({
                configCode: 'mHouseTypeGetRate',
                message: '你好，请问这个户型得房率怎么样？',
              })"
            >
              咨询公摊面积及实用面积 <i class="iconfont icon-chuanghu" style="color:#D8D8D8;font-size: 18px;margin-left: 15rpx;" />
            </span>
            <van-icon class="icon" style="margin-left: auto" name="arrow" />
          </DescriptionItem>
          <DescriptionItem label="贷款成数">
            <span
              @click="handleCollect({
                configCode: 'mHouseTypePercentage',
                message: '你好，请问贷款成数是多少？',
              })"
            >
              了解首套及二套贷款成数 <i class="iconfont icon-jiage1" style="color:#D8D8D8;font-size: 19px;margin-left: 15rpx;" />
            </span>
            <van-icon class="icon" style="margin-left: auto" name="arrow" />
          </DescriptionItem>
          <DescriptionItem label="税费">
            <span
              @click="handleCollect({
                configCode: 'mHouseTypeTaxation',
                message: '你好，请问购房税费怎么算？',
              })"
            >
              新房交易税费详情咨询数 <i class="iconfont icon-wengao" style="color:#D8D8D8;font-size: 18px;margin-left: 15rpx;" />
            </span>
            <van-icon class="icon" style="margin-left: auto" name="arrow" />
          </DescriptionItem>
          <DescriptionItem v-show="detail?.downPaymentThirtyPercent" label="首付占比">
            <span>
              3成  {{ detail?.downPaymentThirtyPercent ? `${detail?.downPaymentThirtyPercent}万` : '' }}，月供约{{ calculator.monthInterest ? `${calculator.monthInterest}元` : '' }}
              <i class="iconfont icon-jisuanqi2" style="color:#D8D8D8;font-size: 18px;margin-left: 15rpx;" @click="toCalculator(detail?.priceTotal)" />
            </span>
            <van-icon class="icon" style="margin-left: auto" name="arrow" />
            <!--            <van-image class="info-icon" :src="iconDesc" @click="toCalculator(detail?.priceTotal)" /> -->
          </DescriptionItem>
        </Description>
      </Card>
    </div>
    <div class="page-margin" @click="showPhoneModal = true">
      <div class="phone-warp">
        <div>
          <div class="phone-context">
            致电售楼处了解更多信息
          </div>
          <div class="phone-sub-text">
            隐私保护,免费咨询
          </div>
        </div>
        <div class="phone-icon">
          <van-image :src="iconPhoto" />
        </div>
      </div>
    </div>
    <div v-show="detail?.floorDiscountList?.length" class="page-margin">
      <Card radius>
        <template #title>
          优惠活动
        </template>
        <template #extra>
          <div
            class="extra-icon"
            @click="handleCollect({
              configCode: 'mHouseTypeDiscount',
              message: '你好，请问有优惠吗？',
            })"
          >
            <van-image class="info-icon" :src="iconCall" />
            咨询优惠
          </div>
        </template>
        <div>
          <NewHouseHouseTypeActivityCard
            v-for="(item, index) in detail?.floorDiscountList"
            :key="`act${index}`"
            :info="item"
            source="新房/M版/户型详情"
          />
        </div>
      </Card>
    </div>
    <!--  户型解读 -->
    <div v-if="detail?.brief" class="page-margin">
      <Card radius>
        <template #title>
          户型解读
        </template>
        <span class="house-type-intro">
          {{ detail?.brief }}
        </span>
      </Card>
    </div>
    <div v-show="caseList?.length" class="page-margin">
      <Card radius>
        <template #title>
          装修灵感
        </template>
        <template #extra>
          <a :href="`/${domain}home/anli/`" class="more-btn">
            查看更多案例
            <van-icon name="arrow" />
          </a>
        </template>
        <div class="decorate-card-warp">
          <Swiper :slides-per-view="2" :space-between="40" :loop="true" class="decorate-card-swp" :list="caseList">
            <template #default="{ data }">
              <NewHouseHouseTypeDecorateCard :config="configMap.mFreeMeasuringRooms" :info="data" />
            </template>
          </Swiper>
        </div>
      </Card>
    </div>
    <div class="page-margin">
      <Card radius>
        <template #title>
          <div>
            优选家居建材
          </div>
        </template>
        <template #extra>
          <a :href="`/${domain}homeshop/t/shoplist.jhtml`" class="more-btn">
            查看更多建材
            <van-icon name="arrow" />
          </a>
        </template>
        <div class="building-materials-warp">
          <div v-for="(item, index) in materialList" :key="index" :class="`div${index + 1}`">
            <a :href="`/${domain}/homeshop/t/${item.shopId}.html`" class="building-materials-image">
              <van-image width="100%" height="100%" :src="$getImageSize(item.indexPic, 'c200x125')" />
            </a>
          </div>
        </div>
      </Card>
    </div>
    <!--  本楼盘其他户型 -->
    <div v-if="otherHouseTypeList.length > 0" class="page-margin">
      <Card radius>
        <template #title>
          <div>
            本楼盘其他户型
          </div>
        </template>
        <Swiper :space-between="-130" class="other-house-model" :list="otherHouseTypeList">
          <template #default="{ data }">
            <NewHouseHouseTypeHouseTypeCard :info="data" />
          </template>
        </Swiper>
      </Card>
    </div>
    <!--  相似户型推荐 -->
    <div v-if="similarHouseTypeList.length > 0" class="page-margin">
      <Card radius>
        <template #title>
          <div>
            相似户型推荐
          </div>
        </template>
        <Swiper :space-between="-130" class="similar-house-model" :list="similarHouseTypeList">
          <template #default="{ data }">
            <NewHouseHouseTypeHouseTypeCard :info="data" />
          </template>
        </Swiper>
      </Card>
    </div>
    <div class="page-margin">
      <Card radius>
        <template #title>
          <div>
            猜你喜欢
          </div>
        </template>
        <div v-for="item in MaybeLikeList" :key="`${item.floorId}_${item.isFavorite}`" class="like-item">
          <NewHouseListItem :item="item" v-bind="item" />
        </div>
      </Card>
    </div>
    <!--  底部按钮  -->
    <NewHouseFloorSticky :house-info="detail" :sort-id="6" :data-id="detail?.floorId" :number-id="Number(route.params.houseModelId || 0)" />
    <!--    拨打隐私号 -->
    <Modal v-model:show="showPhoneModal" :bottom-type="1" title="隐私保护中" content="匿名拨打电话，对方只能看到您的虚拟号码，保护您的隐私" :call-phone="callPhone" />
  </div>
</template>

<style scoped lang="scss">
@import "detail.module";
.like-item {
  padding: 20px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  &:last-child {
    border-bottom: 0;
  }
}
</style>
